import { useEffect, useRef, useState } from 'react';
import { Loader2, Paperclip, X } from 'lucide-react';
import { messagesApi } from '@/lib/api';

export interface ReplyAttachment { storedAs: string; filename: string; mimeType: string; size: number }

export function ReviewAttachments({ value, onChange, onBusyChange, disabled }: {
  value: ReplyAttachment[];
  onChange: (value: ReplyAttachment[]) => void;
  onBusyChange: (busy: boolean) => void;
  disabled: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const active = useRef(true);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { active.current = true; return () => { active.current = false; onBusyChange(false); }; }, []);
  return <div className="space-y-2">
    <input ref={input} type="file" hidden multiple onChange={async event => {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (!files.length || busyRef.current || disabled) return;
      setError('');
      if (value.length + files.length > 5 || files.some(file => file.size > 10 * 1024 * 1024)) {
        setError('Możesz dodać maksymalnie 5 załączników, każdy do 10 MB.'); return;
      }
      busyRef.current = true;
      setBusy(true);
      onBusyChange(true);
      try {
        const uploaded = await messagesApi.uploadAttachments(files);
        if (active.current) onChange([...value, ...uploaded]);
      } catch { if (active.current) setError('Nie udało się dodać załączników.'); }
      finally { busyRef.current = false; if (active.current) { setBusy(false); onBusyChange(false); } }
    }} />
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5" disabled={disabled || busy || value.length >= 5} onClick={() => input.current?.click()}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}Dodaj załącznik
      </button>
      {value.map(file => <span key={file.storedAs} className="inline-flex items-center gap-2 rounded-lg border border-border px-2 py-1 text-xs">
        <Paperclip className="w-3 h-3" /><span className="max-w-48 truncate">{file.filename}</span>
        <button type="button" aria-label={`Usuń załącznik ${file.filename}`} disabled={disabled || busy} onClick={() => onChange(value.filter(item => item.storedAs !== file.storedAs))} className="text-text-muted hover:text-danger"><X className="w-3 h-3" /></button>
      </span>)}
    </div>
    {error && <p role="alert" className="text-xs text-danger">{error}</p>}
  </div>;
}
